import { type ServiceResource, updateService } from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import { ArrowLeft, ExternalLink, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Textarea } from "./ui/textarea";

interface ServiceDetailsPageProps {
	serviceId: string;
	onNavigateBack: () => void;
}

export function ServiceDetailsPage({
	serviceId,
	onNavigateBack,
}: ServiceDetailsPageProps) {
	const { services, companies } = useData();

	const service = useMemo(
		() => services.find((s) => s.id === serviceId),
		[services, serviceId],
	);

	const company = useMemo(
		() => companies.find((c) => c.id === service?.companyId),
		[companies, service],
	);

	const [isEditing, setIsEditing] = useState(false);
	const [editedService, setEditedService] = useState(service);

	if (!service) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Services
					</Button>
				</div>
				<Card>
					<CardContent className="py-8">
						<p className="text-center text-slate-500">Service not found</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const db = getFirestore();

	const handleSave = async () => {
		if (!editedService || !editedService.companyId) return;

		try {
			await updateService(
				db,
				editedService.companyId,
				editedService.id,
				editedService,
			);
			toast.success("Service updated successfully");
			setIsEditing(false);
		} catch (error) {
			console.error("Failed to update service:", error);
			toast.error("Failed to update service");
		}
	};

	const handleCancel = () => {
		setEditedService(service);
		setIsEditing(false);
	};

	const formatTimestamp = (timestamp: Date | string | null | undefined) => {
		return new Date(timestamp ?? "").toLocaleString();
	};

	const getResourceTypeColor = (type: string) => {
		switch (type) {
			case "pdf":
				return "bg-red-100 text-red-800";
			case "doc":
				return "bg-blue-100 text-blue-800";
			case "url":
				return "bg-green-100 text-green-800";
			case "txt":
				return "bg-slate-100 text-slate-800";
			default:
				return "bg-slate-100 text-slate-800";
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Services
					</Button>
				</div>
				<div className="flex gap-2">
					{isEditing ? (
						<>
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
							<Button onClick={handleSave}>Save Changes</Button>
						</>
					) : (
						<Button onClick={() => setIsEditing(true)}>Edit Service</Button>
					)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Service Information</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label>Service ID</Label>
							<Input value={service.id} disabled className="mt-1" />
						</div>
						<div>
							<Label>Company</Label>
							<Input
								value={company?.name || "Unknown"}
								disabled
								className="mt-1"
							/>
						</div>
						<div>
							<Label>Service Name</Label>
							<Input
								value={isEditing ? editedService?.name : service.name}
								onChange={(e) =>
									setEditedService({ ...editedService!, name: e.target.value })
								}
								disabled={!isEditing}
								className="mt-1"
							/>
						</div>
						<div>
							<Label>Website</Label>
							<Input
								value={isEditing ? editedService?.website : service.website}
								onChange={(e) =>
									setEditedService({
										...editedService!,
										website: e.target.value,
									})
								}
								disabled={!isEditing}
								className="mt-1"
							/>
						</div>
						<div className="col-span-2">
							<Label>Description</Label>
							<Textarea
								value={
									isEditing ? editedService?.description : service.description
								}
								onChange={(e) =>
									setEditedService({
										...editedService!,
										description: e.target.value,
									})
								}
								disabled={!isEditing}
								className="mt-1"
								rows={3}
							/>
						</div>
						<div className="col-span-2">
							<Label>Service Brief</Label>
							<Textarea
								value={
									isEditing
										? editedService?.serviceBrief || ""
										: service.serviceBrief || ""
								}
								onChange={(e) =>
									setEditedService({
										...editedService!,
										serviceBrief: e.target.value,
									})
								}
								disabled={!isEditing}
								className="mt-1 min-h-32 break-words whitespace-pre-wrap"
								rows={6}
							/>
						</div>
						<div className="col-span-2">
							<Label>Contact Details</Label>
							<Textarea
								value={
									isEditing
										? editedService?.contactDetails || ""
										: service.contactDetails || ""
								}
								onChange={(e) =>
									setEditedService({
										...editedService!,
										contactDetails: e.target.value,
									})
								}
								disabled={!isEditing}
								className="mt-1"
								rows={4}
							/>
						</div>
						<div>
							<Label>Created Date</Label>
							<Input
								value={formatTimestamp(service.createdAt)}
								disabled
								className="mt-1"
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Business Knowledge</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div>
						<Label>FAQ</Label>
						<Textarea
							value={isEditing ? editedService?.faq || "" : service.faq || ""}
							onChange={(e) =>
								setEditedService({
									...editedService!,
									faq: e.target.value,
								})
							}
							disabled={!isEditing}
							className="mt-1 max-h-72 overflow-y-auto"
							rows={8}
						/>
					</div>
					<div>
						<Label>Knowledge Base</Label>
						<Textarea
							value={
								isEditing
									? editedService?.knowledgeBase || ""
									: service.knowledgeBase || ""
							}
							onChange={(e) =>
								setEditedService({
									...editedService!,
									knowledgeBase: e.target.value,
								})
							}
							disabled={!isEditing}
							className="mt-1 max-h-96 overflow-y-auto"
							rows={12}
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>
						Additional Resources ({service.additionalResources.length})
					</CardTitle>
					<Button size="sm" disabled={!isEditing}>
						<Plus className="h-4 w-4 mr-2" />
						Add Resource
					</Button>
				</CardHeader>
				<CardContent>
					{service.additionalResources.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Resource Name</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Link</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{service.additionalResources.map(
									(resource: ServiceResource, index: number) => (
										<TableRow key={index}>
											<TableCell>{resource.name}</TableCell>
											<TableCell>
												<Badge className={getResourceTypeColor(resource.type)}>
													{resource.type?.toUpperCase() ?? "UNKNOWN"}
												</Badge>
											</TableCell>
											<TableCell className="font-mono text-xs text-slate-600 max-w-md truncate">
												{resource.link}
											</TableCell>
											<TableCell>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => window.open(resource.link, "_blank")}
												>
													<ExternalLink className="h-4 w-4" />
												</Button>
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-8 text-slate-500">
							No additional resources configured
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
